<template>
  <div class="animated fadeIn">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="sk-circle-fade">
        <div class="sk-grid-cube" v-for="n in 9" :key="n"></div>
      </div>
      <h2 class="ml-4 mt-3">Checking premium status...</h2>
    </div>

    <div v-else>
      <!-- Not Premium / Not Allowed Cards -->
      <div v-if="!isPremium">
        <PatronCard :patron="patron" :add_premium="addPremium" />
      </div>
      <div v-else-if="!allowedPersonalization">
        <TierCard
          :patron="patron"
          :add_premium="addPremium"
          :is_server_premium="isPremium"
        />
      </div>

      <!-- Personalization Layout (always shown; disabled when not eligible) -->
      <div class="container">
        <b-card
          class="mx-auto p-0 bg-dark text-white"
          :class="{ 'is-disabled': formDisabled }"
          style="overflow: hidden; border: none"
        >
          <div class="content-wrapper">
            <div class="mb-3 p-3 bg-secondary rounded">
              <h6>
                <i class="fas fa-info-circle mr-2"></i>Personalize Carl-bot for
                your Server
              </h6>
              <small class="text-muted">
                Upload a custom avatar and banner to personalize your bot's
                appearance. Ensure images meet the recommended sizes and
                formats.
                <ul>
                  <li>Supported formats: JPG, PNG, and GIF</li>
                  <li>Max file size: 8MB</li>
                  <li>
                    Recommended dimensions: Avatar 128x128px, Banner 680x240px
                  </li>

                  <li>Dimension limits: Min: 64x64px, Max: 4096x4096px</li>
                </ul>
              </small>
            </div>
          </div>
          <div class="card-header">Profile Preview</div>
          <!-- Banner + Avatar Section -->
          <div class="position-relative banner-wrapper">
            <!-- Banner Background -->
            <div
              class="banner"
              :style="{
                backgroundImage: `url('${customization.banner}')`,
              }"
            ></div>

            <!-- Edit Banner Button -->
            <b-button
              id="banner-btn"
              variant="dark"
              size="sm"
              class="position-absolute edit-banner-btn text-white"
              :disabled="formDisabled || isSaving"
              @click="triggerFile('banner')"
            >
              <i class="fas fa-edit mr-1"></i>Edit Banner
            </b-button>
            <b-tooltip target="banner-btn" placement="left">
              Upload banner image: Max 8MB, Recommended 680x240px
            </b-tooltip>

            <!-- Avatar with Edit Button -->
            <div class="avatar-container">
              <img
                :src="customization.avatar"
                class="avatar-img"
                alt="Avatar"
                @click="triggerFile('avatar')"
              />
              <b-button
                id="avatar-btn"
                variant="outline-dark"
                size="sm"
                class="position-absolute edit-avatar-btn"
                :disabled="formDisabled || isSaving"
                @click="triggerFile('avatar')"
              >
                <i class="fas fa-edit fa-2x text-white"></i>
              </b-button>
              <b-tooltip target="avatar-btn" placement="right">
                Upload avatar image: Max 8MB, Recommended 128x128px
              </b-tooltip>
            </div>
          </div>

          <!-- Hidden File Inputs -->
          <input
            ref="avatarInput"
            type="file"
            class="d-none"
            accept="image/jpeg,image/jpg,image/png,image/gif"
            :disabled="formDisabled || isSaving"
            @change="onFileChange('avatar', $event)"
          />
          <input
            ref="bannerInput"
            type="file"
            class="d-none"
            accept="image/*"
            :disabled="formDisabled || isSaving"
            @change="onFileChange('banner', $event)"
          />

          <div
            class="d-flex flex-column align-items-stretch"
            style="margin-top: 30px"
          >
            <b-button
              class="btn-block"
              variant="primary"
              :disabled="formDisabled || !canSave || isSaving"
              @click="submitPersonalization"
            >
              <span v-if="isSaving">Saving...</span>
              <span v-else>Save Changes</span>
            </b-button>
            <b-button
              class="btn-block"
              variant="danger"
              :disabled="formDisabled || isSaving"
              @click="resetPersonalization"
            >
              Reset
            </b-button>
          </div>

          <!-- Content Section -->
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import PatronCard from "@/views/oc/PatronCard";
import TierCard from "@/views/oc/PatronUpdatedTier";
import miniToastr from "mini-toastr";
import VueNotifications from "vue-notifications";

const toastTypes = {
  success: "success",
  error: "error",
  info: "info",
  warn: "warn",
};
miniToastr.init({ types: toastTypes });

function toast({ title, message, type, timeout, cb }) {
  return miniToastr[type](message, title, timeout, cb);
}

const toast_options = {
  success: toast,
  error: toast,
  info: toast,
  warn: toast,
};

Vue.use(VueNotifications, toast_options);

export default {
  name: "Personalize",
  components: { PatronCard, TierCard },
  data() {
    return {
      loading: true,
      isPremium: false,
      allowedPersonalization: false,
      patron: false,
      customization: { avatar: "", banner: "", is_default: true },

      canSave: false,
      avatarFile: null,
      bannerFile: null,
      isSaving: false,
    };
  },
  computed: {
    formDisabled() {
      return !(this.isPremium && this.allowedPersonalization);
    },
  },
  notifications: {
    showSuccessMsg: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Bot Profile updated successfully!",
    },
    showErrorMsg: {
      type: VueNotifications.types.error,
      title: "Error!",
      message: "Failed to update bot profile!",
    },
  },
  methods: {
    async checkPremiumStatus() {
      try {
        const response = await this.axios.get(
          `/api/v1/servers/${this.$route.params.guild_id}/meta`
        );

        this.isPremium = response.data.is_vip || false;
        this.allowedPersonalization =
          response.data.allowed_personalization || false;
        this.patron = response.data.user_has_premium;
      } catch (error) {
        console.error("Error checking premium status:", error);
        this.isPremium = false;
        this.allowedPersonalization = false;
      } finally {
        this.loading = false;
      }
      await this.loadCustomization();
    },

    async loadCustomization() {
      try {
        const response = await this.axios.get(
          `/api/v1/servers/${this.$route.params.guild_id}/customization`
        );
        let data = response.data;

        // Directly store CDN URLs
        this.customization.avatar = data.avatar;
        this.customization.banner = data.banner;
        this.customization.is_default = data.is_default;

        this.updateCanSave();
      } catch (error) {
        console.error("Error loading customization data:", error);
      }
    },
    async onFileChange(field, event) {
      const file = event.target.files[0];
      const validation = await this.validateImage(file);

      if (!validation.valid) {
        this.showErrorMsg({ message: validation.reason });
        if (event && event.target) event.target.value = null;
        return false;
      }

      if (field === "avatar") {
        this.avatarFile = file;
        this.customization.avatar = URL.createObjectURL(file); // preview
      } else if (field === "banner") {
        this.bannerFile = file;
        this.customization.banner = URL.createObjectURL(file); // preview
      }
      this.updateCanSave();
    },

    async validateImage(file) {
      if (!file) return { valid: false, reason: "No file provided" };
      if (!file.type.startsWith("image/"))
        return {
          valid: false,
          reason: "Invalid image format",
        };
      let max_file_size = 8 * 1024 * 1024;
      if (file.size > max_file_size) {
        return {
          valid: false,
          reason: "Image size exceeds 8MB",
        };
      }

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width < 64 || img.height < 64) {
            resolve({
              valid: false,
              reason: "Image dimensions too small (min 64x64px)",
            });
          } else if (img.width > 4096 || img.height > 4096) {
            resolve({
              valid: false,
              reason: "Image dimensions too large (max 4096x4096px)",
            });
          } else {
            resolve({ valid: true, reason: "" });
          }
        };
        img.onerror = () =>
          resolve({ valid: false, reason: "Invalid image data" });
        img.src = URL.createObjectURL(file);
      });
    },
    updateCanSave() {
      this.canSave = !!(this.avatarFile || this.bannerFile);
    },

    async submitPersonalization() {
      if (this.isSaving) return;
      this.isSaving = true;
      const formData = new FormData();
      if (this.avatarFile) formData.append("avatar", this.avatarFile);
      if (this.bannerFile) formData.append("banner", this.bannerFile);

      try {
        await this.axios.patch(
          `/api/v1/servers/${this.$route.params.guild_id}/customization`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        this.showSuccessMsg();
        this.avatarFile = null;
        this.bannerFile = null;
        if (this.$refs.avatarInput) this.$refs.avatarInput.value = null;
        if (this.$refs.bannerInput) this.$refs.bannerInput.value = null;
        await this.loadCustomization();
        this.updateCanSave();
      } catch (err) {
        if (err.response && err.response.status == 429) {
          this.showErrorMsg({
            message: "You are being rate limited. Please try again later.",
          });
        } else {
          const message =
            err &&
            err.response &&
            err.response.data &&
            (err.response.data.msg || err.response.data.message)
              ? err.response.data.msg || err.response.data.message
              : "Unknown error";
          this.showErrorMsg({
            message: "Failed to update bot profile: " + message,
          });
        }
        await this.loadCustomization();
      } finally {
        this.isSaving = false;
      }
    },

    async resetPersonalization() {
      if (this.isSaving) return;
      if (this.customization.is_default) {
        this.avatarFile = null;
        this.bannerFile = null;
        if (this.$refs.avatarInput) this.$refs.avatarInput.value = null;
        if (this.$refs.bannerInput) this.$refs.bannerInput.value = null;
        await this.loadCustomization();
        this.updateCanSave();
        return;
      }

      this.isSaving = true;
      try {
        await this.axios.delete(
          `/api/v1/servers/${this.$route.params.guild_id}/customization`
        );
        this.showSuccessMsg();
        this.avatarFile = null;
        this.bannerFile = null;
        if (this.$refs.avatarInput) this.$refs.avatarInput.value = null;
        if (this.$refs.bannerInput) this.$refs.bannerInput.value = null;
        await this.loadCustomization();
        this.updateCanSave();
      } catch (err) {
        if (err.response && err.response.status == 429) {
          this.showErrorMsg({
            message: "You are being rate limited. Please try again later.",
          });
        } else {
          const message =
            err &&
            err.response &&
            err.response.data &&
            (err.response.data.msg || err.response.data.message)
              ? err.response.data.msg || err.response.data.message
              : "Unknown error";
          this.showErrorMsg({
            message: "Failed to update bot profile: " + message,
          });
        }
        await this.loadCustomization();
      } finally {
        this.isSaving = false;
      }
    },

    triggerFile(which) {
      if (this.isSaving || this.formDisabled) return;
      if (which === "avatar")
        this.$refs.avatarInput && this.$refs.avatarInput.click();
      if (which === "banner")
        this.$refs.bannerInput && this.$refs.bannerInput.click();
    },

    addPremium() {
      return this.axios
        .post(`/api/v1/servers/${this.$route.params.guild_id}/patron`, {})
        .the(() => {
          this.patron = true;
        });
    },
  },

  async created() {
    this.$Progress.start();

    await this.checkPremiumStatus();
    this.$Progress.finish();
  },
};
</script>

<style scoped>
.banner-wrapper {
  height: 240px;
  background-color: #2f3136;
  position: relative;
}

.banner {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  filter: brightness(0.6);
}

.edit-banner-btn {
  top: 10px;
  right: 10px;
  z-index: 2;
}

.avatar-container {
  position: absolute;
  display: inline-block;
  left: 12px;
  top: 140px;
  /* half of avatar height */
  z-index: 3;
}

.avatar-img {
  width: 128px;
  height: 128px;
  border-radius: 50%;
  border: 6px solid #2f3136;
  object-fit: cover;
  background-color: #000;
}

.edit-avatar-btn {
  position: absolute;
  bottom: 25px;
  right: 25px;
  transform: translate(50%, 50%);
  z-index: 4;
  opacity: 1;
  width: 40px;
  height: 40px;
  /* Remove all button styling */

  border: none !important;
  box-shadow: none !important;
  padding: 8px !important;

  /* Style the icon */
}
</style>
